<template>
    <v-app>
        <header-card-public current_page="login"></header-card-public>
        <v-content class="bg-container">
            <v-layout fill-height row wrap>

                <!-- Left/top Side: Ads -->
                <v-flex v-if="!isMobile" xs12 sm6 class="ads-section">
                    <div class="ads-bg">
                        <public-notifications></public-notifications>
                    </div>
                </v-flex>

                <!-- Right/bottom Side: Welcome Card -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout fill-height align-center justify-center>
                        <div class="login-container">
                            <v-card  class="mx-auto login-card" max-width="450">
                                <v-form @submit.prevent="executeRecaptcha" ref="form">
                                <h3 class="text-xs-center">OTP Verification</h3>
                                
                                <v-card-text>
                                    <v-alert v-if="error" :value="true" color="error" icon="error" class="rounded-4" outline>
                                        {{errorMsg}}
                                    </v-alert>  
                                    <v-alert v-if="info" :value="true" color="info" icon="info" class="rounded-4" outline>
                                        {{infoMsg}}
                                    </v-alert> 
                                    <v-alert v-if="this.$store.state.newDevice" :value="true" color="warning" icon="warning" class="rounded-4" outline>
                                        Login from new device detected.
                                    </v-alert>  

                                    <p class="mt-20">Please enter the OTP we sent to your registered email address.</p>

                                    <v-text-field
                                        ref="otpField"
                                        class="mt-20"
                                        solo
                                        prepend-inner-icon="lock"
                                        v-model="otp"
                                        label="Enter OTP"
                                        :rules="[rules.required, rules.min]"
                                        type="text"
                                        :disabled="disabled"
                                    ></v-text-field>
                                    <span v-if="hide_regenerate && expirationCounter > 0" class="warning--text">
                                        <b>{{ expirationCounter }}</b> seconds remaining until expiration
                                    </span>
                                </v-card-text>
                                <v-divider></v-divider>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn :color="welcomeButtonColor" type="submit" :loading="loading" @click="mode='validate'" class="mr-5 white--text">Verify</v-btn>
                                    <v-btn type="submit" :loading="loading_generate" v-if="!hide_regenerate" @click="mode='regenerate'" class="mr-10">Regenerate</v-btn>
                                </v-card-actions>
                                </v-form>  
                            </v-card>
                        </div>
                        <div class="footer-section caption">
                            <div class="white--text text-xs-center">&copy; Copyright 2024 SweldoNow OPC. All Rights Reserved.</div>
                            <other-legal-docs v-if="!isMobile"></other-legal-docs>
                        </div>
                    </v-layout>
                </v-flex>
                
            </v-layout>
        </v-content>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha"></recaptcha>
    </v-app>
</template>

<script>

import '@/assets/css/public.css'
import '@/assets/css/season.css'
import auth from "@/auth/auth.js";
import Recaptcha from "@/components/ReCaptcha.vue";
import securityApi from "@/api/security.js"
import publicPages from '@/helpers/public-pages.js'
import { getUAData } from '@/helpers/user-agent.js'

export default {
    name: 'Login',
    metaInfo: {
        title: 'Login',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Sign In With Google, Continue with Facebook, Sign In With Email' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        Recaptcha,
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'public-notifications': () => import('@/components/PublicNotifications/PublicNotifications'),
    },
    data () {
        return {
            error: false,
            errorMsg: null,
            info: false,
            infoMsg: null,
            loading: false,
            loading_generate: false,
            disabled: false,
            hide_regenerate: true,
            generateTimer: null,
            otp_timer: 0,
            otp_expiration: 300000, //5mins (300000ms), 1sec=1000ms
            otp: '',
            recaptchResponse: null,

            //todo - put this in rules-helper.js
            rules: {
                required: value => !!value || "OTP is required",
                min: v => (v && v.length === 6) || "OTP must be 6 digits"
            },
            userAgent: null,
        }
    },
    methods: {
        executeRecaptcha() {
            if (this.mode == 'validate') {
            if (this.$refs.form.validate()) {
                // execute the recaptcha widget
                this.$refs.recaptcha.execute()
            }
            } else {
            this.$refs.recaptcha.execute()
            }
        },
        submitReCaptcha(response) {
            this.recaptchResponse = response
            if (this.mode == 'validate') {
                this.validate()
            } else {
                this.regenerate()
            }
        },
        async validate() {
            this.loading = true
            this.disabled = true
            this.error = false

            try {
                const otpPayload = {
                    email: this.$store.state.email,
                    password: this.$store.state.pwd,
                    otp: this.otp,
                    recaptchaResponse: this.recaptchResponse
                }

                const response = await securityApi.verifyOTP(otpPayload)

                if (response.status === 200) {
                    await this.handleFinalLogin()
                }
            } catch (error) {
                this.error = true
                this.info = false
                this.errorMsg = this.parseOTPError(error)
            } finally {
                // Ensure UI is interactive again if an error occurred
                if (this.error) {
                    this.loading = false
                    this.disabled = false
                }
            }
        },
        async handleFinalLogin() {
            const loginData = {
                ...this.getDeviceInfo(), // Spread in a helper for browser/device info
                email: this.$store.state.email,
                password: this.$store.state.pwd,
                recaptchaResponse: this.recaptchResponse,
                sourceType: 'Customer'
            }

            await auth.login(loginData, true);

            if (!auth.loggedIn()) {
                throw new Error("Login failed after OTP verification.");
            }

            auth.clearLoginDetails()

            // Redirection Logic
            if (auth.onBoarded()) {
                const destination = this.$route.query.redirect || '/'
                this.$router.push(destination)
            } else {
                this.$router.push("/features")
            }
        },
        getDeviceInfo() {
            return {
                browserName: this.userAgent.browser.name,
                deviceModel: this.userAgent.device.model,
                deviceType: this.userAgent.device.type,
                deviceVendor: this.userAgent.device.vendor,
                oSName: this.userAgent.os.name,
                oSVersion: this.userAgent.os.version,
                deviceFingerprint: this.userAgent.fingerprint,
                deviceName: this.userAgent.deviceName
            };
        },
        parseOTPError(error) {
            const errorStr = error.toString();

            if (errorStr.includes("409")) {
                this.hide_regenerate = false;
                clearInterval(this.generateTimer);
                return "Invalid or expired OTP.";
            }

            if (errorStr.includes("401")) {
                this.hide_regenerate = true;
                return "You are not authorized to perform this action.";
            }

            // Traditional check for nested properties (No ?. operator)
            if (error.response && error.response.data && error.response.data.message) {
                return error.response.data.message;
            }

            return error.message || "An unexpected error occurred.";
        },
        async regenerate() {
            try {
                this.clearError()
                
                this.disabled = true
                this.loading_generate = true

                //generate otp then send to email
                var data = {
                    email: this.$store.state.email,
                    password: this.$store.state.pwd,
                    recaptchaResponse: this.recaptchResponse
                }
                const response = await securityApi.generateOTP(data)
                if (response.status == 200) {
                    this.info = true
                    this.infoMsg = "New OTP has been created and sent to your email."
                    this.$refs.otpField.focus()
                }

                //enable again regenerate button after 5mins
                clearInterval(this.generateTimer)
                this.otp_timer = 0
                this.disabled = false
                this.generateTimer = setInterval(this.enableRegenerate, 1000)

                this.disabled = false
                this.loading_generate = false
                this.hide_regenerate = true //disable regenerate button until 5mins expiration
            }catch(error){
                this.error = true
                this.info = false
                this.errorMsg = error
            }
        },
        enableRegenerate() {
            this.otp_timer += 1000

            if (this.otp_timer >= this.otp_expiration) {
                // STOP TIMER
                clearInterval(this.generateTimer)

                // FORCE FINAL STATE
                this.otp_timer = this.otp_expiration

                // SHOW EXPIRED ALERT
                this.error = true
                this.info = false
                this.errorMsg = "OTP has expired. Please regenerate a new one."

                // ENABLE REGENERATE BUTTON
                this.hide_regenerate = false

                // OPTIONAL: Disable OTP input
                this.disabled = true
            }
        },
        clearError() {
            this.error = false
            this.info = false
            this.errorMsg = null
            this.otp = ''
        },
        startOtpTimer() {
            if (this.generateTimer) clearInterval(this.generateTimer);
            this.generateTimer = setInterval(this.enableRegenerate, 1000);
        }
    },
    mounted() {
        // Focus the text field when component is mounted
        this.$nextTick(() => {
            if (this.$refs.otpField) {
                this.$refs.otpField.focus()
            }
        });
    },
    async created () {
        const redirectPath = this.$route.query.redirect || '/';

        // 1. Immediate redirect if already logged in (no flicker)
        if (auth.loggedIn()) {
            return this.$router.push(redirectPath);
        }

        // 2. Security Check: Block manual access if credentials are missing
        if (!this.$store.state.email || !this.$store.state.pwd) {
            return this.$router.push({
                path: '/login',
                query: { redirect: this.$route.query.redirect }
            });
        }

        // 3. Initialize background tasks
        try {
            this.userAgent = await getUAData();
            this.startOtpTimer();
        } catch (err) {
            console.error("Failed to load device data:", err);
        }
    },
    destroyed() {
        clearInterval(this.generateTimer)
    },
    computed: {
        expirationCounter() {
            const remaining = (this.otp_expiration - this.otp_timer) / 1000
            return remaining > 0 ? remaining : 0
        },
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcomeSectionClass() {
            return this.seasonDetails.className;
        },
        welcomeButtonColor() {
            return this.seasonDetails.buttonColor;
        },
    }
}
</script>